import { Injectable, OnDestroy, computed, inject, signal } from "@angular/core";
import { NotificationTrayStorage } from "./notification-tray-storage";
import { NOTIFICATION_TRAY_ENABLED } from "./notification-tray.token";
import {
  VpFloatingToast,
  VpNotification,
  VpNotificationTone,
  VpNotifyInput,
  VpNotifyOptions
} from "./notification.models";

const TRANSIENT_TIMEOUT_MS = 5000;
const STICKY_TUCK_MS = 6000;
const RESOLVED_TUCK_MS = 1500;
const UNDO_WINDOW_MS = 6000;
/** Applies separately to locked and non-locked floating entries. */
const FLOAT_LIMIT = 3;
const TRAY_CAP = 200;
/** How many event ids `notify` remembers for replay detection; the oldest are forgotten first. */
const SOURCE_ID_CAP = 500;

type Timeout = ReturnType<typeof setTimeout>;

interface PendingTimer {
  action: () => void;
  remainingMs: number;
  startedAt: number;
  /** `null` while paused. */
  handle: Timeout | null;
}

/** An unresolved urgent notification: it can't be dismissed, tucked or evicted. */
function isLocked(n: VpNotification): boolean {
  return n.urgent && !n.resolved;
}

/** An unresolved call light; a newer activation from the same device supersedes it. */
function isOpenLight(n: VpNotification): boolean {
  return n.kind === "call-light" && !!n.deviceUserId && !n.resolved;
}

function isTransient(entry: VpFloatingToast): boolean {
  return entry.notification.kind === "transient";
}

function coerceMessage(message: unknown): string {
  if (typeof message === "string") {
    return message;
  }
  // Callers routinely hand over caught errors despite the `string` signature.
  const nested = (message as { message?: unknown } | null | undefined)?.message;
  return typeof nested === "string" ? nested : String(message);
}

/** Evicts the oldest non-locked items past the cap. Expects newest-first order. */
function enforceCap(items: VpNotification[]): VpNotification[] {
  let excess = items.length - TRAY_CAP;
  if (excess <= 0) {
    return items;
  }
  const evicted = new Set<string>();
  for (let i = items.length - 1; i >= 0 && excess > 0; i--) {
    if (!isLocked(items[i])) {
      evicted.add(items[i].id);
      excess--;
    }
  }
  return items.filter(n => !evicted.has(n.id));
}

function newestFirst(items: VpNotification[]): VpNotification[] {
  return [...items].sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Owns all notification state: short-lived transients, the floating stack and the
 * per-user tray. Rendering lives in the notification host, which reads these signals.
 */
@Injectable({ providedIn: "root" })
export class NotificationService implements OnDestroy {
  private readonly storage = inject(NotificationTrayStorage);
  private readonly trayEnabled = inject(NOTIFICATION_TRAY_ENABLED);

  private readonly floatingState = signal<VpFloatingToast[]>([]);
  private readonly trayState = signal<VpNotification[]>([]);
  private readonly undoSnapshot = signal<VpNotification[] | null>(null);

  /** Oldest first, in the order entries started floating. */
  readonly floating = this.floatingState.asReadonly();
  /** Newest first by `createdAt`. */
  readonly tray = this.trayState.asReadonly();
  readonly trayCount = computed(() => this.trayState().length);
  readonly urgentCount = computed(() => this.trayState().filter(isLocked).length);
  /** Locked, unacknowledged items waiting for a floating slot. */
  readonly urgentOverflow = computed(() => {
    const floatingIds = new Set(this.floatingState().map(entry => entry.id));
    return this.trayState().filter(n => isLocked(n) && !n.acknowledged && !floatingIds.has(n.id))
      .length;
  });
  readonly clearedCount = computed(() => this.trayState().filter(n => n.resolved).length);
  readonly hasDismissable = computed(() => this.trayState().some(n => !isLocked(n)));
  readonly undoCount = computed(() => this.undoSnapshot()?.length ?? null);

  private userId: string | null = null;
  private readonly timers = new Map<string, PendingTimer>();
  /** Floating ids under the pointer or focus; timers started for them begin paused. */
  private readonly pausedIds = new Set<string>();
  private undoTimer: Timeout | null = null;
  private idCounter = 0;
  /**
   * Event id → the notification id it first produced, in insertion order. Event
   * subscriptions replay the latest event to every new subscriber, so the same event
   * reaches `notify` more than once. Kept in memory only, and not reset by `setUser`:
   * an event already raised in this app session must never raise again.
   */
  private readonly notifiedSources = new Map<string, string>();

  successMessage(
    message: string,
    title: string | null = "Success",
    options: VpNotifyOptions = {}
  ): void {
    this.showMessage("success", message, title, options);
  }

  errorMessage(
    message: string,
    title: string | null = "Error",
    options: VpNotifyOptions = {}
  ): void {
    this.showMessage("danger", message, title, options);
  }

  infoMessage(message: string, title: string | null = "Info", options: VpNotifyOptions = {}): void {
    this.showMessage("info", message, title, options);
  }

  warningMessage(
    message: string,
    title: string | null = "Warning",
    options: VpNotifyOptions = {}
  ): void {
    this.showMessage("warning", message, title, options);
  }

  /**
   * Adds a notification to the tray and floats it. Returns its id. A `sourceId` seen
   * before is a replay: nothing changes and the id first created for it is returned.
   */
  notify(input: VpNotifyInput): string {
    const seen = input.sourceId ? this.notifiedSources.get(input.sourceId) : undefined;
    if (seen !== undefined) {
      return seen;
    }

    const notification: VpNotification = {
      ...input,
      // Urgency locks an item on screen until it's opened or cleared, and leans on the
      // tray to hold the ones past the floating limit. With no tray, nothing is locked.
      urgent: this.trayEnabled() && input.urgent,
      id: this.nextId(),
      createdAt: Date.now(),
      resolved: false,
      acknowledged: false
    };

    if (this.trayEnabled()) {
      // A repeat activation from the same device replaces its open item instead of stacking.
      const deviceUserId =
        notification.kind === "call-light" ? notification.deviceUserId : undefined;
      const replaced = (n: VpNotification): boolean =>
        !!deviceUserId && isOpenLight(n) && n.deviceUserId === deviceUserId;

      const tray = enforceCap([notification, ...this.trayState().filter(n => !replaced(n))]);
      this.commitTray(tray);
      // Float before promoting, so a replacement keeps the slot its predecessor held.
      if (tray.includes(notification)) {
        this.float(notification, null);
      }
      this.promoteOverflow();
    } else {
      this.floatUntilDismissed(notification);
    }

    if (input.sourceId) {
      this.rememberSource(input.sourceId, notification.id);
    }
    return notification.id;
  }

  /** Freezes the entry's pending timer until `resume`. */
  pause(id: string): void {
    if (!this.floatingState().some(entry => entry.id === id)) {
      return;
    }
    this.pausedIds.add(id);
    const timer = this.timers.get(id);
    if (timer?.handle != null) {
      clearTimeout(timer.handle);
      timer.handle = null;
      timer.remainingMs = Math.max(0, timer.remainingMs - (Date.now() - timer.startedAt));
    }
  }

  resume(id: string): void {
    if (!this.pausedIds.delete(id)) {
      return;
    }
    const timer = this.timers.get(id);
    if (timer && timer.handle === null) {
      this.runTimer(id, timer);
    }
  }

  /** Removes a non-locked item from floating and the tray. */
  dismiss(id: string): void {
    const tray = this.trayState();
    const item = tray.find(n => n.id === id);
    if (!item) {
      this.removeFloating([id]);
      return;
    }
    if (isLocked(item)) {
      return;
    }
    this.commitTray(tray.filter(n => n.id !== id));
  }

  /** Removes a non-locked entry from floating; a tray item stays in the tray. */
  tuck(id: string): void {
    const entry = this.floatingState().find(e => e.id === id);
    if (entry && !isLocked(entry.notification)) {
      this.removeFloating([id]);
    }
  }

  /** Marks an item seen and stops floating it, even when locked. */
  acknowledge(id: string): void {
    const tray = this.trayState();
    if (tray.some(n => n.id === id && !n.acknowledged)) {
      this.commitTray(tray.map(n => (n.id === id ? { ...n, acknowledged: true } : n)));
    }
    this.removeFloating([id]);
    this.promoteOverflow();
  }

  /** Tucks every non-locked tray-backed entry; transients keep floating. */
  tuckAll(): void {
    // Without a tray to tuck into, removing an entry from the stack would lose it.
    if (!this.trayEnabled()) {
      return;
    }
    this.removeFloating(
      this.floatingState()
        .filter(entry => !isTransient(entry) && !isLocked(entry.notification))
        .map(entry => entry.id)
    );
  }

  /** Removes every non-locked tray item, undoable for a short window. */
  dismissAll(): void {
    const tray = this.trayState();
    const removed = tray.filter(n => !isLocked(n));
    if (removed.length === 0) {
      return;
    }
    this.commitTray(tray.filter(isLocked));
    this.clearUndo();
    this.undoSnapshot.set(removed);
    this.undoTimer = setTimeout(() => this.clearUndo(), UNDO_WINDOW_MS);
  }

  undoDismissAll(): void {
    const snapshot = this.undoSnapshot();
    if (!snapshot) {
      return;
    }
    this.clearUndo();
    const tray = this.trayState();
    const present = new Set(tray.map(n => n.id));
    // A light re-activated since the dismiss supersedes its dismissed item, as in `notify`.
    const openDevices = new Set(tray.filter(isOpenLight).map(n => n.deviceUserId));
    const restored = snapshot.filter(
      n => !present.has(n.id) && !(isOpenLight(n) && openDevices.has(n.deviceUserId))
    );
    if (restored.length > 0) {
      this.commitTray(enforceCap(newestFirst([...tray, ...restored])));
    }
  }

  /** Removes every resolved item. */
  dismissCleared(): void {
    const tray = this.trayState();
    if (tray.some(n => n.resolved)) {
      this.commitTray(tray.filter(n => !n.resolved));
    }
  }

  /**
   * Resolves a device's open call-light items. A clear older than an item's activation
   * belongs to an earlier activation (a replayed or stale event) and is ignored.
   */
  markCleared(deviceUserId: string, by: string | null, at: number): void {
    const clears = (n: VpNotification): boolean =>
      isOpenLight(n) && n.deviceUserId === deviceUserId && (n.activatedAt ?? n.createdAt) <= at;
    const resolve = (n: VpNotification): VpNotification =>
      clears(n) ? { ...n, resolved: true, resolvedBy: by, resolvedAt: at } : n;

    // Dismissed items waiting on undo are resolved too, so an undo restores them as Cleared.
    const snapshot = this.undoSnapshot();
    if (snapshot?.some(clears)) {
      this.undoSnapshot.set(snapshot.map(resolve));
    }

    const tray = this.trayState();
    const cleared = new Set(tray.filter(clears).map(n => n.id));
    if (cleared.size === 0) {
      return;
    }
    this.commitTray(tray.map(resolve));
    // Leave the "Cleared" state on screen briefly before tucking.
    for (const entry of this.floatingState()) {
      if (cleared.has(entry.id)) {
        this.startTimer(entry.id, RESOLVED_TUCK_MS, () => this.removeFloating([entry.id]));
      }
    }
    this.promoteOverflow();
  }

  /**
   * Switches the tray to a user's persisted items. Transients survive the switch;
   * everything tray-backed from the previous user is dropped.
   */
  setUser(userId: string | null): void {
    this.userId = userId;
    this.clearUndo();
    this.removeFloating(
      this.floatingState()
        .filter(entry => !isTransient(entry))
        .map(entry => entry.id)
    );
    // Loading isn't a mutation, so it isn't written back. With the tray off a stored tray
    // is left untouched rather than read, so turning it back on restores what was there.
    this.trayState.set(
      userId === null || !this.trayEnabled()
        ? []
        : enforceCap(newestFirst(this.storage.read(userId)))
    );
    this.promoteOverflow();
  }

  ngOnDestroy(): void {
    for (const timer of this.timers.values()) {
      if (timer.handle !== null) {
        clearTimeout(timer.handle);
      }
    }
    this.timers.clear();
    this.pausedIds.clear();
    this.clearUndo();
  }

  private showMessage(
    tone: VpNotificationTone,
    message: unknown,
    title: string | null,
    options: VpNotifyOptions
  ): void {
    const text = coerceMessage(message);
    const heading = title ?? "";
    const html = !!options.enableHtml;

    if (options.disableTimeOut) {
      this.notify({
        kind: "message",
        tone,
        heading,
        message: text,
        html,
        sticky: true,
        urgent: false
      });
      return;
    }

    if (
      this.floatingState().some(entry => isTransient(entry) && entry.notification.message === text)
    ) {
      return;
    }

    this.float(
      {
        id: this.nextId(),
        kind: "transient",
        tone,
        heading,
        message: text,
        html,
        sticky: false,
        urgent: false,
        createdAt: Date.now(),
        resolved: false,
        acknowledged: false
      },
      options.timeOut ?? TRANSIENT_TIMEOUT_MS
    );
  }

  /** Adds an entry to the floating stack under the per-group limits and starts its timer. */
  private float(notification: VpNotification, countdownMs: number | null): void {
    const floating = this.floatingState();
    const entry: VpFloatingToast = { id: notification.id, notification, countdownMs };

    if (isLocked(notification)) {
      // Past the limit a locked item waits in the tray and is counted by `urgentOverflow`.
      if (floating.filter(e => isLocked(e.notification)).length < FLOAT_LIMIT) {
        this.floatingState.set([...floating, entry]);
      }
      return;
    }

    const next = [...floating, entry];
    const nonLocked = next.filter(e => !isLocked(e.notification));
    const evicted = nonLocked.slice(0, Math.max(0, nonLocked.length - FLOAT_LIMIT));
    this.floatingState.set(next.filter(e => !evicted.includes(e)));
    evicted.forEach(e => this.forgetTimer(e.id));

    // A transient expires after its countdown; a tray-backed entry tucks.
    this.startTimer(notification.id, countdownMs ?? STICKY_TUCK_MS, () =>
      this.removeFloating([notification.id])
    );
  }

  /**
   * Floats an entry with no countdown and no stack limit, the way toastr's
   * `disableTimeOut` toasts behaved before the tray: it leaves when the user dismisses it
   * and not before. Used while the tray is off, where an evicted or expired entry would
   * have nowhere to wait and would simply be lost.
   */
  private floatUntilDismissed(notification: VpNotification): void {
    this.floatingState.set([
      ...this.floatingState(),
      { id: notification.id, notification, countdownMs: null }
    ]);
  }

  /** Fills free locked slots with the oldest overflowed items. */
  private promoteOverflow(): void {
    const floating = this.floatingState();
    const free = FLOAT_LIMIT - floating.filter(e => isLocked(e.notification)).length;
    if (free <= 0) {
      return;
    }
    const floatingIds = new Set(floating.map(e => e.id));
    const promoted = this.trayState()
      .filter(n => isLocked(n) && !n.acknowledged && !floatingIds.has(n.id))
      .reverse()
      .slice(0, free);
    if (promoted.length > 0) {
      this.floatingState.set([
        ...floating,
        ...promoted.map(notification => ({ id: notification.id, notification, countdownMs: null }))
      ]);
    }
  }

  /**
   * The single write path for the tray: keeps floating entries pointing at the current
   * item objects, drops entries whose item left the tray, and persists for the signed-in user.
   */
  private commitTray(next: VpNotification[]): void {
    this.trayState.set(next);

    const byId = new Map(next.map(n => [n.id, n]));
    let changed = false;
    const floating: VpFloatingToast[] = [];
    for (const entry of this.floatingState()) {
      const current = byId.get(entry.id);
      if (current === entry.notification) {
        floating.push(entry);
      } else if (current) {
        changed = true;
        floating.push({ ...entry, notification: current });
      } else if (isTransient(entry)) {
        floating.push(entry);
      } else {
        changed = true;
        this.forgetTimer(entry.id);
      }
    }
    if (changed) {
      this.floatingState.set(floating);
    }

    if (this.userId !== null && this.trayEnabled()) {
      this.storage.write(this.userId, next);
    }
  }

  private removeFloating(ids: readonly string[]): void {
    if (ids.length === 0) {
      return;
    }
    const remove = new Set(ids);
    const floating = this.floatingState();
    const next = floating.filter(entry => !remove.has(entry.id));
    if (next.length !== floating.length) {
      this.floatingState.set(next);
    }
    ids.forEach(id => this.forgetTimer(id));
  }

  private startTimer(id: string, ms: number, action: () => void): void {
    this.clearTimer(id);
    const timer: PendingTimer = { action, remainingMs: ms, startedAt: 0, handle: null };
    this.timers.set(id, timer);
    if (!this.pausedIds.has(id)) {
      this.runTimer(id, timer);
    }
  }

  private runTimer(id: string, timer: PendingTimer): void {
    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => {
      timer.handle = null;
      if (this.timers.get(id) === timer) {
        this.timers.delete(id);
      }
      timer.action();
    }, timer.remainingMs);
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer?.handle != null) {
      clearTimeout(timer.handle);
    }
    this.timers.delete(id);
  }

  /** Drops all per-entry bookkeeping once an entry stops floating. */
  private forgetTimer(id: string): void {
    this.clearTimer(id);
    this.pausedIds.delete(id);
  }

  private clearUndo(): void {
    if (this.undoTimer !== null) {
      clearTimeout(this.undoTimer);
      this.undoTimer = null;
    }
    this.undoSnapshot.set(null);
  }

  private rememberSource(sourceId: string, id: string): void {
    this.notifiedSources.set(sourceId, id);
    if (this.notifiedSources.size > SOURCE_ID_CAP) {
      const [oldest] = this.notifiedSources.keys();
      this.notifiedSources.delete(oldest);
    }
  }

  private nextId(): string {
    return typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${this.idCounter++}`;
  }
}
